import { formNames } from '@wkda/carlead-creation-form';
import { ANCHOR, AnchorStateProvider, Wizard, usePageData } from '@wkda/funnel-components';
import { findInput, getAllInputs } from '@wkda/funnel-utils';
import { memo } from 'react';
import { Field } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import { getArrayOfPreviousSteps, getGraphTails } from '../Fields/hooks/useStateMachineForFields';
import { usePersistDecorator } from '../formCache';
import { getStateValueStrings } from '../FormsStateMachine/stateSelectors';
import { useCarleadConflicts } from '../hooks/useCarleadConflicts';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const CarleadWizard = /*#__PURE__*/memo(CarleadWizardBase);
function CarleadWizardBase(_ref) {
  let {
    children,
    carleadForm,
    options,
    submitHandler,
    topContent,
    bottomContent,
    machineService
  } = _ref;
  const persistDecorator = usePersistDecorator();
  const {
    getSubmissionErrors,
    setErrors
  } = useCarleadConflicts();
  const location = useLocation();
  function onSubmitHandler(values, currentStep, isLastStep, formApi) {
    const payload = carleadForm.mapValuesToPayload({}, values);
    let promise = !isLastStep ? Promise.resolve(null) : submitHandler({
      requestPayload: payload,
      extraParams: {
        step: currentStep + 1,
        formValues: values,
        isLastStep,
        machineService,
        formApi,
        carleadForm
      },
      response: undefined
    });
    return promise;
  }
  function validate(currentStep, values) {
    return carleadForm.validateStep(currentStep, values);
  }
  function onValidationErrorHandler(_ref2, _ref3, errorsPerPage) {
    var _getState$errors, _getState$values;
    let {
      getState
    } = _ref2;
    let {
      currentStep
    } = _ref3;
    const errors = (_getState$errors = getState().errors) !== null && _getState$errors !== void 0 ? _getState$errors : {};
    const keys = Object.keys(errors);
    const snapshot = machineService.getSnapshot();
    const value = snapshot.value;
    const context = snapshot.context;
    const currentStateFields = getStateValueStrings(value);
    const currentStateField = keys.filter(item => {
      return currentStateFields.some(state => {
        return state.includes(item);
      });
    }).at(0);
    const currentFields = getGraphTails(context.selectedFields).flatMap(item => {
      return getArrayOfPreviousSteps(context.previousSelectedFieldsMap, item, true);
    });
    // if the currentFields is empty we assume the current step is the first step
    // by pushing the currentStateField
    if (currentFields.length === 0 && currentStateField !== undefined) {
      currentFields.push(currentStateField);
    }
    carleadForm.lifecycleHooks.onValidationError({
      errors,
      values: (_getState$values = getState().values) !== null && _getState$values !== void 0 ? _getState$values : {},
      currentStateField,
      currentFields
    });
    onFocusHandler(getState().errors, currentStep, errorsPerPage);
  }
  function onSubmitSuccessHandler(step, isLastStep, response, setStep, values) {
    var _response$car;
    const stepFields = carleadForm.getStepFields(step);
    carleadForm.lifecycleHooks.onStepSubmitSuccess({
      step,
      stepFields,
      isLastStep,
      carleadHash: response === null || response === void 0 || (_response$car = response.car) === null || _response$car === void 0 ? void 0 : _response$car.hash,
      setStep,
      response,
      values,
      location
    });
  }
  function onSubmitErrorHandler(_, __, e, _setStep) {
    setErrors(e);
    return getSubmissionErrors(e);
  }

  // comes from the server initial page data extracted from WKDA cookie
  const {
    latestCIDchannel
  } = usePageData();
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(AnchorStateProvider, {
      stepAnchor: ANCHOR,
      enableStepAnchor: options === null || options === void 0 ? void 0 : options.enableStepAnchor,
      children: /*#__PURE__*/_jsx("div", {
        className: "form-wizard",
        children: /*#__PURE__*/_jsxs(Wizard, {
          validate: validate,
          totalSteps: 1,
          getErrorsPerStep: carleadForm.validateAllSteps,
          initialValues: carleadForm.generateInitialValues(),
          onSubmit: onSubmitHandler,
          onValidationError: onValidationErrorHandler,
          onSubmitSuccess: onSubmitSuccessHandler,
          onSubmitError: onSubmitErrorHandler,
          onBeforeSubmit: (formAPI, _ref4) => {
            let {
              setInitialValues
            } = _ref4;
            const {
              values,
              invalid
            } = formAPI.getState();
            if (!invalid) {
              setInitialValues(values);
            }
          },
          parseResponse: (_, values) => values,
          topContent: topContent,
          bottomContent: bottomContent,
          decorators: [persistDecorator.persistDecorator],
          children: [children, /*#__PURE__*/_jsx(Field, {
            name: formNames.latestCIDchannel,
            type: "hidden",
            component: "input",
            initialValue: latestCIDchannel
          })]
        })
      })
    })
  });
}
function onFocusHandler() {
  var _findInput;
  let errors = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let currentStep = arguments.length > 1 ? arguments[1] : undefined;
  let errorsPerPage = arguments.length > 2 ? arguments[2] : undefined;
  let stepWithErrors = errorsPerPage.findIndex(i => {
    return Object.keys(i).length > 0;
  });
  const firstErrorKey = Object.keys(errorsPerPage[stepWithErrors] || {})[0];
  const sameSection = stepWithErrors === currentStep;
  const foundElement = (_findInput = findInput(getAllInputs(), errors)) !== null && _findInput !== void 0 ? _findInput : getElement(firstErrorKey);
  if (foundElement) {
    setTimeout(() => {
      if (typeof foundElement.focus === 'function') {
        foundElement.focus({
          preventScroll: true
        });
      }
      foundElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, sameSection ? 0 : 1000);
  }
}
function getElement(element) {
  return document.getElementById(element) || document.getElementsByName(element)[0] || document.getElementsByClassName(element)[0];
}